import React from "react";
import { useTranslation } from "react-i18next";
import { useProtocolStats, useAllEscrows, useTokenMetadata } from "../hooks/useEscrow";

/** Format a raw bigint amount with thousands separators and compact notation for large values. */
function formatAmount(val: bigint, decimals: number): string {
  if (val === 0n) return "0";
  const divisor = 10n ** BigInt(decimals);
  const whole = val / divisor;
  const frac = val % divisor;

  // Compact notation for big numbers (>= 1M)
  const wholeNum = Number(whole);
  if (wholeNum >= 1_000_000) {
    return new Intl.NumberFormat("en-US", {
      notation: "compact",
      maximumFractionDigits: 2,
    }).format(wholeNum);
  }

  // Build fractional part with up to 2 digits
  let fracStr = "";
  if (frac !== 0n && decimals > 0) {
    const fullFrac = frac.toString().padStart(decimals, "0").slice(0, 2).replace(/0+$/, "");
    if (fullFrac) fracStr = "." + fullFrac;
  }

  return new Intl.NumberFormat("en-US").format(wholeNum) + fracStr;
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { data: stats, isLoading } = useProtocolStats();
  const { data: escrows } = useAllEscrows();
  // Derive token decimals from the first valid escrow (pragmatic: most escrows use the same token)
  const firstToken = escrows?.find((e) => !/^0x0+$/.test(e.token.toLowerCase()))?.token;
  const { data: tokenMeta } = useTokenMetadata(firstToken);
  const decimals = tokenMeta?.decimals ?? 8;
  const unit = tokenMeta?.symbol ?? t("dash.unit_tokens");

  const disputeRate = stats && stats.totalEscrows > 0n
    ? ((Number(stats.totalDisputes) / Number(stats.totalEscrows)) * 100).toFixed(1) + "%"
    : "—";

  return (
    <div className="page">
      {/* ── Hero ── */}
      <div className="hero">
        <div className="hero-content">
          <div className="hero-eyebrow">{t("dash.hero_eyebrow")}</div>
          <h1>
            {t("dash.hero_title1")}<br />
            <span className="grad-text">{t("dash.hero_title2")}</span>
          </h1>
          <p className="hero-sub">
            {t("dash.hero_sub")}
          </p>
          <div className="hero-actions">
            <a href="/create" className="btn btn-primary btn-lg">{t("dash.btn_create")}</a>
            <a href="/arbitrators" className="btn btn-secondary">{t("dash.btn_arb")}</a>
          </div>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="section-header">
        <h2 className="section-title">{t("dash.overview")}</h2>
        {isLoading && (
          <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{t("dash.syncing")}</span>
        )}
      </div>

      {isLoading && !stats ? (
        <div className="stats-grid">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="stat-card">
              <div className="skeleton" style={{ height: 14, width: 80, marginBottom: 12 }} />
              <div className="skeleton" style={{ height: 32, width: 100 }} />
            </div>
          ))}
        </div>
      ) : stats ? (
        <div className="stats-grid">
          <StatCard label={t("dash.stat_total_escrows")}   value={stats.totalEscrows.toString()} gradient />
          <StatCard label={t("dash.stat_active_escrows")}  value={stats.activeEscrows.toString()} />
          <StatCard label={t("dash.stat_total_volume")}    value={formatAmount(stats.totalVolume, decimals)} unit={unit} />
          <StatCard label={t("dash.stat_total_fees")}      value={formatAmount(stats.totalFees, decimals)}   unit={unit} />
          <StatCard label={t("dash.stat_total_disputes")}  value={stats.totalDisputes.toString()} />
          <StatCard label={t("dash.stat_resolved")}        value={stats.totalResolved.toString()} />
          <StatCard label={t("dash.stat_expired")}         value={stats.totalExpired.toString()} />
          <StatCard label={t("dash.stat_dispute_rate")}    value={disputeRate} />
        </div>
      ) : (
        <div className="stats-grid">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="stat-card">
              <div className="stat-label">
                {[
                  t("dash.stat_total_escrows"),
                  t("dash.stat_active_escrows"),
                  t("dash.stat_total_volume"),
                  t("dash.stat_total_fees"),
                  t("dash.stat_total_disputes"),
                  t("dash.stat_resolved"),
                  t("dash.stat_expired"),
                  t("dash.stat_dispute_rate")
                ][i]}
              </div>
              <div className="stat-value" style={{ color: "var(--text-muted)" }}>—</div>
              <div className="stat-unit">{t("dash.no_indexer")}</div>
            </div>
          ))}
        </div>
      )}

      <div className="dashboard-links">
        <a href="/create" className="btn btn-primary">{t("dash.btn_create_pl")}</a>
        <a href="/escrows" className="btn btn-secondary">Ver Escrows</a>
        <a href="/arbitrators" className="btn btn-secondary">{t("dash.btn_browse_arb")}</a>
        <a href="/reputation" className="btn btn-ghost">{t("dash.btn_rep_exp")}</a>
      </div>
    </div>
  );
}

function StatCard({
  label, value, unit, gradient,
}: {
  label: string;
  value: string | number;
  unit?: string;
  gradient?: boolean;
}) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className={`stat-value${gradient ? " grad-text" : ""}`}>{value.toString()}</div>
      {unit && <div className="stat-unit">{unit}</div>}
    </div>
  );
}
